import React, { useState, useEffect } from "react";
import {
  TextInput,
  Group,
  Button,
  Stack,
  Text,
  ActionIcon,
  Tooltip,
  Modal,
  Box,
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconEye,
  IconEdit,
  IconRefresh,
  IconTrash,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useClient } from "../../context/ClientContext";
import { useSalon } from "../../context/SalonContext";
import usePermissions from "../../hooks/usePermissions";
import Permissioned from "../common/Permissioned";
import ClientForm from "./ClientForm";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { formatDateTime } from "../../utils/timeUtils";
import styles from "./ClientsList.module.css";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import { StandardTable } from "../common/StandardTable";
import PageHeader from "../common/PageHeader";
import DuplicatesBanner from "./merge/DuplicatesBanner";
import MergeSelectionBar from "./merge/MergeSelectionBar";

const MAX_MERGE_SELECTION = 3;

function ClientsList() {
  const navigate = useNavigate();
  const perms = usePermissions();
  const clientPerms = perms?.clients;
  // In the new permission model, canEdit is merged into canView
  const canEditClients = clientPerms?.canView !== false;
  const {
    clients,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalClients,
    sorting,
    setSorting,
    refreshClients,
    deleteClient,
    potentialDuplicates,
  } = useClient();
  const { businessInfo } = useSalon();

  // Merging is destructive (it deletes the losing records), so the whole
  // select-and-merge affordance is gated behind delete permission.
  const canMergeClients = clientPerms?.canDelete === true;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [selectedMergeIds, setSelectedMergeIds] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const toggleMergeRow = (client) => {
    setSelectedMergeIds((prev) => {
      if (prev.includes(client.id)) {
        return prev.filter((id) => id !== client.id);
      }
      if (prev.length >= MAX_MERGE_SELECTION) {
        return prev;
      }
      return [...prev, client.id];
    });
  };

  const startMerge = () => {
    navigate("/admin/clients/merge", {
      state: { clientIds: selectedMergeIds },
    });
  };

  const duplicateCount = potentialDuplicates?.length ?? 0;
  const showBanner = canMergeClients && duplicateCount > 0 && !bannerDismissed;

  useEffect(() => {
    if (selectedClient) {
      const updatedClient = clients.find(
        (client) => client.id === selectedClient.id,
      );
      if (updatedClient) {
        setSelectedClient(updatedClient);
      }
    }
  }, [clients]);

  const handleSort = (field) => {
    setSorting((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    // Sorting reorders the entire client list, so jump back to the first page
    // to show the top of the newly sorted results (not the current page number).
    setPage(1);
  };

  const columns = [
    {
      key: "first_name",
      label: "First Name",
      sortable: true,
      render: (c) => c.first_name,
    },
    {
      key: "last_name",
      label: "Last Name",
      sortable: true,
      render: (c) => c.last_name,
    },
    {
      key: "phone",
      label: "Phone",
      sortable: true,
      render: (c) => formatPhoneNumber(`${c.area_code}${c.phone_number}`),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: (c) => c.email || "-",
    },
    {
      key: "last_appointment",
      label: "Last Appointment",
      sortable: true,
      render: (c) =>
        c.last_appointment
          ? formatDateTime(
              c.last_appointment,
              businessInfo?.timezone,
              "MMM D, YYYY h:mm A",
            )
          : "-",
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      width: 140,
      render: (client) => (
        <Group gap="xs" justify="flex-end">
          <Tooltip label="View Details">
            <ActionIcon
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/clients/${client.id}`);
              }}
            >
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
          <Permissioned.Action canDo={canEditClients}>
            <Tooltip label="Edit">
              <ActionIcon
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedClient(client);
                  setIsCreateModalOpen(true);
                }}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Tooltip>
          </Permissioned.Action>
          <Permissioned.Action canDo={clientPerms?.canDelete}>
            <Tooltip label="Delete">
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  setClientToDelete(client);
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Permissioned.Action>
        </Group>
      ),
    },
  ];

  const handleDeleteConfirm = async () => {
    try {
      await deleteClient(clientToDelete.id);
      setClientToDelete(null);
      if (selectedClient?.id === clientToDelete.id) {
        setSelectedClient(null);
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  const headerActions = (
    <Group gap="sm" wrap="nowrap">
      <TextInput
        placeholder="Search clients..."
        leftSection={<IconSearch size={16} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        w={260}
        aria-label="Search clients"
      />
      <Tooltip label="Refresh">
        <ActionIcon
          variant="default"
          size="lg"
          onClick={refreshClients}
          aria-label="Refresh clients"
        >
          <IconRefresh size={18} />
        </ActionIcon>
      </Tooltip>
      <Permissioned.Action canDo={canEditClients}>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          New Client
        </Button>
      </Permissioned.Action>
    </Group>
  );

  return (
    <Box className={styles.container}>
      <BrandedLoadingOverlay visible={loading} />
      <Box p={{ base: "sm", xs: "md", sm: "lg" }}>
        <Stack gap="md">
          <PageHeader title="Clients" actions={headerActions} />

          {showBanner && (
            <DuplicatesBanner
              count={duplicateCount}
              onReview={() => navigate("/admin/clients/duplicates")}
              onDismiss={() => setBannerDismissed(true)}
            />
          )}

          {error && (
            <Text color="red" size="sm" className={styles.error}>
              {error}
            </Text>
          )}

          <StandardTable
            columns={columns}
            data={clients}
            getRowKey={(c) => c.id}
            ariaLabel="Clients"
            selectable={canMergeClients}
            selectedKeys={new Set(selectedMergeIds)}
            onToggleRow={toggleMergeRow}
            isRowSelectable={() =>
              selectedMergeIds.length < MAX_MERGE_SELECTION
            }
            sortField={sorting.field}
            sortDirection={sorting.direction}
            onSort={handleSort}
            emptyState={
              <Text c="dimmed" size="sm">
                {searchQuery
                  ? "No clients match your search."
                  : "No clients yet."}
              </Text>
            }
            pagination={{
              page,
              pageSize,
              total: totalClients,
              onPageChange: setPage,
              onPageSizeChange: (size) => {
                setPageSize(size);
                setPage(1);
              },
              pageSizeOptions: ["25", "50"],
              itemLabel: "clients",
            }}
          />
        </Stack>
      </Box>

      <Modal
        opened={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedClient(null);
        }}
        title={selectedClient ? "Edit Client" : "New Client"}
        size="lg"
        centered
      >
        <Box className={styles.modalContent}>
          <ClientForm
            client={selectedClient}
            onClose={() => {
              setIsCreateModalOpen(false);
              setSelectedClient(null);
            }}
            readOnly={!canEditClients}
          />
        </Box>
      </Modal>

      <DeleteConfirmationModal
        isOpen={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Client"
        message={`Are you sure you want to delete ${clientToDelete?.first_name} ${clientToDelete?.last_name}? This action cannot be undone.`}
      />

      {canMergeClients && (
        <MergeSelectionBar
          count={selectedMergeIds.length}
          max={MAX_MERGE_SELECTION}
          onClear={() => setSelectedMergeIds([])}
          onMerge={startMerge}
        />
      )}
    </Box>
  );
}

export default ClientsList;
